import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  useSpecNarrativeSource,
  useSpecReadLocale,
  useSpecProjectDetail,
  useCheckSpecFlow,
  useSpecNextStep,
  useUpdateSpecCharter,
  useUpdateSpecFeatures,
  useUpdateSpecValidationList,
  useUpdateSpecDecisionLog,
  useUpdateSpecProjectNotes,
  useUpdateSpecDesignLinks,
  useSpecFlowSource,
  useUpdateSpecFlow,
  useRetireSpecFlow,
  useRestoreSpecFlow,
  useUpdateSpecNarrative,
} from '../../../hooks/useSpecRequirements.js';
import { CHARTER_FIELDS, CharterEditor } from './CharterEditor.js';
import { FormatHint } from './FormatHint.js';
import { ProjectStatusCards } from './ProjectStatusCards.js';
import { ZoomDialog } from './FlowSection.js';
import { FlowSections } from './FlowSections.js';
import { EditableSection, type SpecSection } from './EditableSection.js';
import { FeaturesSection } from './FeaturesSection.js';
import { IntegrationTestSection } from './IntegrationTestSection.js';
import { ValidationListSection } from './ValidationListSection.js';
import { DecisionLogSection } from './DecisionLogSection.js';
import { ProjectNotesSection } from './ProjectNotesSection.js';
import { DesignLinksSection } from './DesignLinksSection.js';
import { NarrativeEditor } from './NarrativeEditor.js';
import { useSpecReviewActions } from './useSpecReviewActions.js';
import { SINGLETON_REVIEW_TARGETS } from '../../../lib/api/spec-requirements.js';

export interface SpecRequirementsViewProps {
  /** 開く案件。一覧の行かプロジェクト一覧の行から渡される */
  pjKey: string;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義の成果物（憲章・ナラティブ・業務フロー・機能一覧・バリデーション一覧・決定記録・総合テスト・補足欄・デザイン成果物・レビュー状態）を
 *   1 画面で読み、各成果物の編集とレビュー操作（依頼・指摘解決・DLE 承認/差し戻し）を行う
 * @graph-connects /spec-requirements [calls] 成果物の取得
 * @graph-connects CharterEditor [calls] 憲章の編集
 * @graph-connects NarrativeEditor [calls] ナラティブの編集
 * @graph-connects FeaturesSection [calls] 機能一覧の編集
 * @graph-connects ValidationListSection [calls] バリデーション一覧の編集
 * @graph-connects DecisionLogSection [calls] 決定記録の編集
 * @graph-connects ProjectNotesSection [calls] 補足欄の編集
 * @graph-connects DesignLinksSection [calls] デザイン成果物の編集
 * @graph-connects IntegrationTestSection [calls] 総合テストのシナリオ一覧
 * @graph-connects ReviewActions [calls] レビュー操作
 * @graph-connects ProjectStatusCards [calls] 次にやること・PL の承認状況
 */
export function SpecRequirementsView({ pjKey }: SpecRequirementsViewProps) {
  const { t } = useTranslation();
  const readLocale = useSpecReadLocale();
  const selected = pjKey;
  // 同時に開くのは 1 節だけ。別の節を開いたら前の下書きは畳む
  const [editing, setEditing] = useState<SpecSection>('');
  const [zoom, setZoom] = useState('');
  const [warnings, setWarnings] = useState<{ section: SpecSection; items: string[] }>({
    section: '',
    items: [],
  });
  // 業務フローは案件内に複数あるので、節と違い flowId で直前に保存した図を絞り込む
  const [flowWarnings, setFlowWarnings] = useState<{ flowId: string; items: string[] }>({
    flowId: '',
    items: [],
  });

  // 直リンク（pjKey 指定）では一覧を使わないので、一覧 API の失敗・遅延を道連れにしない
  const detailQuery = useSpecProjectDetail(selected);
  const detail = detailQuery.data ?? null;
  const nextStep = useSpecNextStep(selected);
  const save = useUpdateSpecNarrative(selected);
  // 編集フォームには画面言語に関わらず常に原文（ja）を渡す。画面言語の narrative を渡すと、
  // 訳が fresh なときにその訳文が編集初期値になり、保存で原文が上書きされてしまう
  // 編集フォームには画面の言語に関わらずその成果物の原文を渡す。原文の言語はレスポンスが返す
  // （`ja` 決め打ちにすると、原文が英語の成果物を日本語の訳で上書きしてしまう）。
  // まだ成果物が無い新規作成では、書く人の画面の言語がそのまま原文の言語になる
  const narrativeSource = useSpecNarrativeSource(
    selected,
    editing === 'narrative',
    detail?.narrative?.sourceLocale ?? readLocale,
  );
  const saveCharter = useUpdateSpecCharter(selected);
  const saveFeatures = useUpdateSpecFeatures(selected);
  const saveValidationList = useUpdateSpecValidationList(selected);
  const saveDecisionLog = useUpdateSpecDecisionLog(selected);
  const saveProjectNotes = useUpdateSpecProjectNotes(selected);
  const saveDesignLinks = useUpdateSpecDesignLinks(selected);
  const saveFlow = useUpdateSpecFlow(selected);
  const checkFlow = useCheckSpecFlow();
  const retireFlow = useRetireSpecFlow(selected);
  const restoreFlow = useRestoreSpecFlow(selected);
  // 業務フローは案件に複数あるので、どの図を編集中かを flowId で持つ
  const [editingFlow, setEditingFlow] = useState('');
  // 編集フォームにはナラティブと同じくその図の原文を渡す（訳を載せると保存で原文が消える）
  const flowSource = useSpecFlowSource(
    selected,
    editingFlow,
    editingFlow !== '',
    detail?.flows.find((flow) => flow.flowId === editingFlow)?.sourceLocale ?? readLocale,
  );

  /** 保存時の注意は、直前に保存した節にだけ出す。 */
  const warningsFor = (section: SpecSection): string[] =>
    warnings.section === section ? warnings.items : [];

  /** 業務フローの保存時の注意は、直前に保存したその図にだけ出す。 */
  const flowWarningsFor = (flowId: string): string[] =>
    flowWarnings.flowId === flowId ? flowWarnings.items : [];

  /** 開いていた節・図の下書き・失敗表示を畳む。 */
  const closeAll = () => {
    setEditing('');
    setEditingFlow('');
    saveFlow.reset();
    checkFlow.reset();
    setWarnings({ section: '', items: [] });
    setFlowWarnings({ flowId: '', items: [] });
    save.reset();
    saveCharter.reset();
    saveFeatures.reset();
    saveValidationList.reset();
    saveDecisionLog.reset();
    saveProjectNotes.reset();
    saveDesignLinks.reset();
  };

  /**
   * 節 1 つぶんのレビュー操作。
   *
   * 業務フローと同じものを案件に 1 つの成果物にも置く。`targetId` は成果物名そのもの
   * （API 側の取り決め）。配線は `useSpecReviewActions` が持つ。
   */
  const reviewActionsFor = useSpecReviewActions(selected);

  /**
   * 案件に 1 つの節（機能一覧・バリデーション一覧・決定記録・補足欄・デザイン成果物）に共通の props。
   *
   * 節ごとに書き分けると「この節だけ注意が出ない」「この節だけ閉じない」のような差が入り込む。
   * 成果物ごとに違うのは保存する値の型とレビュー操作の有無だけなので、`onSave` と `actions` は
   * 呼び出し側に残す（補足欄とデザイン成果物はレビュー対象ではないので `actions` を渡さない）。
   */
  const plainSection = (
    section: SpecSection,
    mutation: { isPending: boolean; error: unknown; reset: () => void },
  ) => ({
    editing: editing === section,
    warnings: warningsFor(section),
    saving: mutation.isPending,
    error: mutation.error,
    onEdit: () => {
      openSection(section);
    },
    onCancel: () => {
      mutation.reset();
      setEditing('');
    },
  });

  /** 保存が通ったら残った注意を節の見出しの下に出し、その節を畳む。 */
  const afterSave = (section: SpecSection) => ({
    onSuccess: (result: { warnings: string[] }) => {
      setWarnings({ section, items: result.warnings });
      setEditing('');
    },
  });

  /** 1 節だけ開く。別の節が開いていたらそちらは畳む。 */
  const openSection = (section: SpecSection) => {
    closeAll();
    setEditing(section);
  };
  const error = detailQuery.error !== null ? t('spec.view.readFailed') : '';

  const narrative = detail?.narrative ?? null;
  // 案件名は空文字のことがある（古い成果物）ので ?? では代替にならない
  const title = useMemo(() => {
    const named = detail?.pjTitle ?? '';
    return named === '' ? (detail?.pjKey ?? '') : named;
  }, [detail?.pjTitle, detail?.pjKey]);

  if (error !== '')
    return <p className="p-6 text-sm text-[var(--color-text-secondary)]">{error}</p>;

  return (
    <div className="p-6">
      <Link
        to="/projects/spec"
        className="text-sm text-[var(--color-accent-primary)] hover:underline"
      >
        {t('spec.view.back')}
      </Link>

      {detail === null ? (
        <p className="mt-6 text-sm text-[var(--color-text-muted)]">{t('spec.loading')}</p>
      ) : (
        <article className="mt-6">
          <header className="border-b-2 border-[var(--color-border-hover)] pb-3">
            <h2 className="text-lg font-bold">{title}</h2>
            <p className="text-sm text-[var(--color-text-muted)] mt-1 flex gap-4">
              <span>{detail.pjKey}</span>
              <a
                className="text-[var(--color-accent-primary)] hover:underline"
                href={`https://air-closet.backlog.jp/view/${detail.pjKey}`}
              >
                Backlog
              </a>
            </p>
          </header>

          <ProjectStatusCards pjKey={selected} step={nextStep.data ?? null} />

          <EditableSection
            heading={t('spec.artifact.charter')}
            action={t(detail.charter === null ? 'spec.action.write' : 'spec.action.edit')}
            editing={editing === 'charter'}
            warnings={warningsFor('charter')}
            onEdit={() => {
              openSection('charter');
            }}
          >
            {editing === 'charter' ? (
              <CharterEditor
                charter={detail.charter}
                saving={saveCharter.isPending}
                error={saveCharter.error}
                onSave={(input) => {
                  saveCharter.mutate(input, {
                    onSuccess: (result) => {
                      setWarnings({ section: 'charter', items: result.warnings });
                      setEditing('');
                    },
                  });
                }}
                onCancel={() => {
                  saveCharter.reset();
                  setEditing('');
                }}
              />
            ) : detail.charter === null ? (
              <p className="mt-3 text-sm text-[var(--color-text-muted)]">
                {detail.charterReadFailure === 'specSheetUnreadable'
                  ? t('spec.charter.specSheetUnreadable')
                  : detail.charterReadFailure === 'error'
                    ? t('spec.charter.readError')
                    : t('spec.charter.empty')}
              </p>
            ) : (
              <>
                {/* 訳が無い・古いときは原文が返る。読んでいるものが原文だと分かるように断る */}
                {detail.charter.untranslated === true ? (
                  <p className="mt-3 text-xs text-[var(--color-text-muted)]">
                    {t('spec.charter.untranslated')}
                  </p>
                ) : null}
                {detail.charter.source === 'specSheet' ? (
                  <p className="mt-3 text-xs text-[var(--color-text-muted)]">
                    {t('spec.charter.fromSpecSheet')}
                  </p>
                ) : null}
                <dl className="mt-3 space-y-3">
                  {CHARTER_FIELDS.filter(([key]) => detail.charter?.[key] !== '').map(
                    ([key, labelKey]) => (
                      <div key={key}>
                        <dt className="text-xs font-semibold text-[var(--color-text-secondary)]">
                          {t(labelKey)}
                        </dt>
                        <dd className="ml-0 whitespace-pre-wrap">{detail.charter?.[key]}</dd>
                      </div>
                    ),
                  )}
                </dl>
              </>
            )}
          </EditableSection>

          <EditableSection
            heading={t('spec.narrative.heading')}
            version={narrative?.version}
            action={t(narrative === null ? 'spec.action.write' : 'spec.action.edit')}
            editing={editing === 'narrative'}
            warnings={warningsFor('narrative')}
            onEdit={() => {
              openSection('narrative');
            }}
          >
            {editing === 'narrative' ? (
              <FormatHint artifact="narrative" label={t('spec.artifact.narrative')} />
            ) : null}
            {editing === 'narrative' ? (
              narrativeSource.data === undefined ? (
                <p className="mt-3 text-sm text-[var(--color-text-muted)]">{t('spec.loading')}</p>
              ) : (
                <NarrativeEditor
                  narrative={narrativeSource.data}
                  saving={save.isPending}
                  error={save.error}
                  onSave={(input) => {
                    save.mutate(input, {
                      onSuccess: (result) => {
                        setWarnings({ section: 'narrative', items: result.warnings });
                        setEditing('');
                      },
                    });
                  }}
                  onCancel={() => {
                    save.reset();
                    setEditing('');
                  }}
                />
              )
            ) : narrative === null ? (
              <p className="mt-3 text-sm text-[var(--color-text-muted)]">
                {t('spec.narrative.empty')}
              </p>
            ) : (
              <>
                {/* 訳が無い・古いときは原文が返る。空で隠されていないので、
                      読んでいるものが原文だと分かるように断る */}
                {narrative.untranslated === true ? (
                  <p className="mt-3 text-xs text-[var(--color-text-muted)]">
                    {t('spec.narrative.untranslated')}
                  </p>
                ) : null}
                <dl className="mt-3 space-y-3">
                  {(
                    [
                      ['why', narrative.why],
                      ['change', narrative.change],
                      ['scope', narrative.scope],
                      ['verify', narrative.verify],
                    ] as const
                  ).map(([section, body]) => (
                    <div key={section}>
                      <dt className="text-xs font-semibold text-[var(--color-text-secondary)]">
                        {t(`spec.narrative.sections.${section}.heading`)}
                      </dt>
                      <dd className="ml-0 whitespace-pre-wrap">{body}</dd>
                    </div>
                  ))}
                </dl>
                {narrative.open.length > 0 ? (
                  <aside className="mt-4 border-l-4 border-[var(--color-warning)] bg-[var(--color-warning-muted)] px-4 py-3 rounded-r">
                    <h3 className="text-xs font-semibold text-[var(--color-warning-text)]">
                      {t('spec.narrative.openItems.legend')}
                    </h3>
                    <ul className="mt-1 text-sm list-disc pl-5 text-[var(--color-text-primary)]">
                      {narrative.open.map((o) => (
                        <li key={o}>{o}</li>
                      ))}
                    </ul>
                  </aside>
                ) : null}
              </>
            )}
            {editing === 'narrative' || narrative === null
              ? null
              : reviewActionsFor(
                  SINGLETON_REVIEW_TARGETS.narrative,
                  t('spec.artifact.narrative'),
                  narrative.version,
                  narrative.review,
                )}
          </EditableSection>

          <FlowSections
            flows={detail.flows}
            editingFlowId={editingFlow}
            source={flowSource.data}
            saving={saveFlow.isPending}
            saveError={saveFlow.error}
            checking={checkFlow.isPending}
            checkResult={checkFlow.data ?? null}
            warningsFor={flowWarningsFor}
            onZoom={setZoom}
            onCheck={(input) => {
              checkFlow.mutate(input);
            }}
            onEdit={(flowId) => {
              closeAll();
              setEditingFlow(flowId);
            }}
            onSave={(flowId, input) => {
              saveFlow.mutate(
                { flowId, input },
                {
                  onSuccess: (result) => {
                    setEditingFlow('');
                    checkFlow.reset();
                    setFlowWarnings({ flowId, items: result.warnings });
                  },
                },
              );
            }}
            onCancel={() => {
              setEditingFlow('');
              saveFlow.reset();
              checkFlow.reset();
            }}
            retireFor={(flowId) => ({
              pending: retireFlow.isPending || restoreFlow.isPending,
              error:
                retireFlow.variables?.flowId === flowId
                  ? retireFlow.error
                  : restoreFlow.variables === flowId
                    ? restoreFlow.error
                    : null,
              onRetire: (reason) => {
                retireFlow.mutate({ flowId, reason });
              },
              onRestore: () => {
                restoreFlow.mutate(flowId);
              },
            })}
            reviewActionsFor={reviewActionsFor}
          />

          <FeaturesSection
            features={detail.features}
            {...plainSection('features', saveFeatures)}
            onSave={(rows) => {
              saveFeatures.mutate(rows, afterSave('features'));
            }}
            actions={
              editing === 'features' || detail.features === null
                ? null
                : reviewActionsFor(
                    SINGLETON_REVIEW_TARGETS.features,
                    t('spec.artifact.features'),
                    detail.features.version,
                    detail.features.review,
                  )
            }
          />

          <ValidationListSection
            validationList={detail.validationList}
            {...plainSection('validationList', saveValidationList)}
            onSave={(rows) => {
              saveValidationList.mutate(rows, afterSave('validationList'));
            }}
            actions={
              editing === 'validationList' || detail.validationList === null
                ? null
                : reviewActionsFor(
                    SINGLETON_REVIEW_TARGETS.validationList,
                    t('spec.artifact.validationList'),
                    detail.validationList.version,
                    detail.validationList.review ?? null,
                  )
            }
          />

          <DecisionLogSection
            decisionLog={detail.decisionLog}
            {...plainSection('decisionLog', saveDecisionLog)}
            onSave={(rows) => {
              saveDecisionLog.mutate(rows, afterSave('decisionLog'));
            }}
            actions={
              editing === 'decisionLog' || detail.decisionLog === null
                ? null
                : reviewActionsFor(
                    SINGLETON_REVIEW_TARGETS.decisionLog,
                    t('spec.artifact.decisionLog'),
                    detail.decisionLog.version,
                    detail.decisionLog.review ?? null,
                  )
            }
          />

          <IntegrationTestSection
            pjKey={detail.pjKey}
            integrationTest={detail.integrationTest}
            actions={
              detail.integrationTest === null
                ? null
                : reviewActionsFor(
                    SINGLETON_REVIEW_TARGETS.integrationTest,
                    t('spec.artifact.integrationTest'),
                    detail.integrationTest.version,
                    detail.integrationTest.review ?? null,
                  )
            }
          />

          {/* 補足欄とデザイン成果物は cortex で作る成果物ではなくその余白なので、6 つの成果物の
                後ろに並べる。レビュー操作は渡さない（どちらもレビュー対象ではない）。
                デザイン成果物だけ担当者以外（Interface Design チーム）も保存できるが、その判定は
                API 側にあるので画面の書き方は補足欄と同じになる */}
          <ProjectNotesSection
            projectNotes={detail.projectNotes ?? null}
            {...plainSection('projectNotes', saveProjectNotes)}
            onSave={(body) => {
              saveProjectNotes.mutate(body, afterSave('projectNotes'));
            }}
          />

          <DesignLinksSection
            designLinks={detail.designLinks ?? null}
            {...plainSection('designLinks', saveDesignLinks)}
            onSave={(links) => {
              saveDesignLinks.mutate(links, afterSave('designLinks'));
            }}
          />
        </article>
      )}

      {zoom === '' ? null : (
        <ZoomDialog
          svg={zoom}
          onClose={() => {
            setZoom('');
          }}
        />
      )}
    </div>
  );
}
